import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Row, Col, Card, Button, Input, Select, message, Tabs, Badge, Space } from 'antd';
import { Animated } from "react-animated-css";
import {
	SettingOutlined,
	ReloadOutlined,
	ExportOutlined,
	MonitorOutlined,
	AlertOutlined,
	CloudServerOutlined,
	BarChartOutlined
} from '@ant-design/icons';

import LiveStreamMonitor from './components/LiveStreamMonitor/LiveStreamMonitor';
import StreamPreview from './components/StreamPreview/StreamPreview';
import ContentModeration from './components/ContentModeration/ContentModeration';
import SystemStatus from './components/SystemStatus/SystemStatus';
import StreamStats from './components/StreamStats/StreamStats';

import './StreamManagement.scss';

// module
import { liveprogramList } from '../../modules/liveprogram';

const { Search } = Input;
const { Option } = Select;

class StreamManagement extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			refreshing: false,
			activeTab: 'monitor',
			
			// Live streams data
			liveStreams: [],
			selectedStream: null,
			totalStreams: 0,
			currentPage: 1,
        	pageSize: 10,
			
			// Filter states
			searchKeyword: '',
			statusFilter: 'all',
			categoryFilter: 'all',
			qualityFilter: 'all',
			sortField: 'viewerCount',
			sortOrder: 'descend',
			
			// System status
			systemStatus: {
				totalServers: 0,
				activeServers: 0,
				totalBandwidth: 0,
				cdnNodes: 0,
				alerts: []
			},
			
			// Reports and moderation
			pendingReports: [],
			moderationQueue: [],
			
			// Stream statistics
			streamStats: {
				totalLiveStreams: 0,
				totalViewers: 0,
				averageQuality: 0,
				bandwidthUsage: 0
			}
		};
	}

	componentDidMount() {
		this.loadData();
		// this.startRealTimeUpdates();
	}

	componentWillUnmount() {
		this.stopRealTimeUpdates();
	}

	// Load all data
	loadData = () => {
    	this.setState({ loading: true });
		
		return Promise.all([  // 加上 return
			this.loadLiveStreams(),
			this.loadSystemStatus(),
			this.loadPendingReports(),
			this.loadStreamStats()
		]).finally(() => {
			this.setState({ loading: false });
		});
	}

	// Start real-time updates
	startRealTimeUpdates = () => {
		this.updateInterval = setInterval(() => {
			this.loadLiveStreams();
			this.loadSystemStatus();
			this.loadStreamStats();
		}, 5000); // Update every 5 seconds
	}

	// Stop real-time updates
	stopRealTimeUpdates = () => {
		if (this.updateInterval) {
			clearInterval(this.updateInterval);
		}
	}

	// Load live streams
	loadLiveStreams = () => {
		const { currentPage, pageSize} = this.state;
		const rawParams = {
			search: this.state.searchKeyword || undefined,
			status: this.state.statusFilter !== 'all' ? this.state.statusFilter : undefined,
			showAll: true,
			page: currentPage,
        	perPage: pageSize,
		};

		const params = Object.fromEntries(
			Object.entries(rawParams).filter(([key, value]) => value !== undefined)
		);


		this.props.liveprogramList(params)
			.then((res) => {
				this.setState({
					liveStreams: res.results,
					totalStreams: res.total,
				});
			})
	}

	handlePaginationChange = (page, pageSize) => {
		this.setState({ 
			currentPage: page, 
			pageSize: pageSize 
		}, () => {
			this.loadLiveStreams();
		});
	}

	// Load system status
	loadSystemStatus = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const mockStatus = {
					totalServers: 12,
					activeServers: 11,
					totalBandwidth: 2500,
					cdnNodes: 8,
					alerts: [
						{
							id: 1,
							type: 'warning',
							message: 'High CPU usage on Server 3',
							timestamp: new Date().toISOString()
						},
						{
							id: 2,
							type: 'info',
							message: 'CDN cache updated successfully',
							timestamp: new Date().toISOString()
						}
					]
				};

				this.setState({ systemStatus: mockStatus });
				resolve(mockStatus);
			}, 800);
		});
	}

	// Load pending reports
	loadPendingReports = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const mockReports = [
					{
						id: 1,
						streamId: 1,
						reportType: 'inappropriate_content',
						description: 'Inappropriate language used',
						reportedBy: 'user123',
						timestamp: new Date().toISOString(),
						status: 'pending'
					},
					{
						id: 2,
						streamId: 2,
						reportType: 'spam',
						description: 'Spam messages in chat',
						reportedBy: 'user456',
						timestamp: new Date().toISOString(),
						status: 'pending'
					}
				];

				const mockModerationQueue = [
					{
						id: 1,
						streamId: 1,
						action: 'review_required',
						priority: 'high',
						assignedTo: 'moderator1',
						timestamp: new Date().toISOString()
					}
				];

				this.setState({
					pendingReports: mockReports,
					moderationQueue: mockModerationQueue
				});
				resolve({ reports: mockReports, queue: mockModerationQueue });
			}, 600);
		});
	}

	// Load stream statistics
	loadStreamStats = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const mockStats = {
					totalLiveStreams: 156,
					totalViewers: 12450,
					averageQuality: 85,
					bandwidthUsage: 2.8,
					peakViewers: 18200,
					averageStreamDuration: 145,
					topCategories: [
						{ category: 'Gaming', count: 65, percentage: 41.7 },
						{ category: 'Music', count: 32, percentage: 20.5 },
						{ category: 'Lifestyle', count: 28, percentage: 17.9 },
						{ category: 'Education', count: 20, percentage: 12.8 },
						{ category: 'Sports', count: 11, percentage: 7.1 }
					],
					viewersByHour: [
						{ hour: '00:00', viewers: 8500 },
						{ hour: '01:00', viewers: 7200 },
						{ hour: '02:00', viewers: 6100 },
						{ hour: '03:00', viewers: 5800 },
						{ hour: '04:00', viewers: 6200 },
						{ hour: '05:00', viewers: 7500 },
						{ hour: '06:00', viewers: 9200 },
						{ hour: '07:00', viewers: 11800 },
						{ hour: '08:00', viewers: 14500 },
						{ hour: '09:00', viewers: 16200 },
						{ hour: '10:00', viewers: 18200 },
						{ hour: '11:00', viewers: 17800 }
					]
				};

				this.setState({ streamStats: mockStats });
				resolve(mockStats);
			}, 700);
		});
	}

	// Tab change handler
	handleTabChange = (key) => {
		this.setState({ activeTab: key });
		
		// Load specific data based on tab
		switch (key) {
			case 'monitor':
				this.loadLiveStreams();
				break;
			case 'moderation':
				this.loadPendingReports();
				break;
			case 'statistics':
				this.loadStreamStats();
				break;
			case 'system':
				this.loadSystemStatus();
				break;
			default:
				break;
		}
	}

	// Refresh handler
	handleRefresh = () => {
		this.setState({ refreshing: true });
		this.loadData().finally(() => {
			this.setState({ refreshing: false });
			message.success('Data refreshed successfully');
		});
	}

	// Export handler
	handleExport = () => {
		const { activeTab } = this.state;
		message.info(`Exporting ${activeTab} data...`);
		
		// Mock export functionality
		setTimeout(() => {
			message.success(`${activeTab} data exported successfully`);
		}, 2000);
	}

	// Settings handler
	handleSettings = () => {
		message.info('Opening settings...');
	}

	// Stream selection handler
	handleStreamSelect = (stream) => {
		this.setState({ selectedStream: stream });
	}

	// Report resolution handler
	handleReportResolve = (reportId, action) => {
		const { pendingReports } = this.state;
		const updatedReports = pendingReports.filter(report => report.id !== reportId);
		
		this.setState({ pendingReports: updatedReports });
		message.success(`Report ${reportId} ${action} successfully`);
	}

	// Moderation action handler
	handleModerationAction = (action, streamId) => {
		message.success(`${action} applied to stream ${streamId}`);
		
		// Update moderation queue
		const { moderationQueue } = this.state;
		const updatedQueue = moderationQueue.filter(item => item.streamId !== streamId);
		this.setState({ moderationQueue: updatedQueue });
	}

	// System refresh handler
	handleSystemRefresh = () => {
		this.loadSystemStatus();
		message.success('System status refreshed');
	}

	// Filter handlers
	handleSearchChange = (value) => {
		this.setState({ searchKeyword: value }, () => {
			this.loadLiveStreams();
		});
	}

	handleFilterChange = (filterType, value) => {
		this.setState({ [filterType]: value }, () => {
			this.loadLiveStreams();
		});
	}

	render() {
		const {
			loading,
			refreshing,
			activeTab,
			liveStreams,
			selectedStream,
			systemStatus,
			pendingReports,
			moderationQueue,
			streamStats,
			currentPage,
			pageSize,
			totalStreams,
		} = this.state;

		// Tab items configuration
		const tabItems = [
			// {
			// 	key: 'statistics',
			// 	label: (
			// 		<span>
			// 			<BarChartOutlined />
			// 			Statistics
			// 		</span>
			// 	),
			// 	children: (
			// 		<div className="tab-content">
			// 			<StreamStats 
			// 				stats={streamStats}
			// 				streams={liveStreams}
			// 				loading={loading}
			// 			/>
			// 		</div>
			// 	)
			// },
			{
				key: 'monitor',
				label: (
					<span>
						<MonitorOutlined />
						Live Monitor
					</span>
				),
				children: (
					<Row gutter={[24, 24]} className="tab-content">
						<Col xs={24} lg={selectedStream ? 14 : 24}>
							<LiveStreamMonitor 
								streams={liveStreams}
								loading={loading}
								onStreamSelect={this.handleStreamSelect}
								onRefresh={this.handleRefresh}
								selectedStream={selectedStream}
								onSearchChange={this.handleSearchChange}
								onFilterChange={this.handleFilterChange}
								isNarrowLayout={!!selectedStream}
								currentPage={currentPage}
								pageSize={pageSize}
								total={totalStreams}
								onPaginationChange={this.handlePaginationChange}
							/>
						</Col>
						
						{selectedStream && (
							<Col xs={24} lg={10}>
								<StreamPreview 
									stream={selectedStream}
									onClose={() => this.setState({ selectedStream: null })}
								/>
							</Col>
						)}
					</Row>
				)
			},
			// {
			// 	key: 'moderation',
			// 	label: (
			// 		<span>
			// 			<AlertOutlined />
			// 			Moderation
			// 			{pendingReports.length > 0 && (
			// 				<Badge 
			// 					count={pendingReports.length} 
			// 					size="small" 
			// 					style={{ marginLeft: 8 }}
			// 				/>
			// 			)}
			// 		</span>
			// 	),
			// 	children: (
			// 		<div className="tab-content">
			// 			<ContentModeration 
			// 				reports={pendingReports}
			// 				moderationQueue={moderationQueue}
			// 				loading={loading}
			// 				onReportResolve={this.handleReportResolve}
			// 				onModerationAction={this.handleModerationAction}
			// 			/>
			// 		</div>
			// 	)
			// },
			// {
			// 	key: 'system',
			// 	label: (
			// 		<span>
			// 			<CloudServerOutlined />
			// 			System Status
			// 		</span>
			// 	),
			// 	children: (
			// 		<div className="tab-content">
			// 			<SystemStatus 
			// 				status={systemStatus}
			// 				loading={loading}
			// 				onRefresh={this.handleSystemRefresh}
			// 			/>
			// 		</div>
			// 	)
			// }
		];

		return (
			<Animated animationIn="fadeIn" animationOut="fadeOut" isVisible={true}>
				<div className="stream-management-wrapper">
					{/* Page Header */}
					<div className="page-header">
						<div className="header-left">
							<h2>Stream Management</h2>
							<p>Monitor live streams and manage content moderation</p>
						</div>
						<div className="header-actions">
							{/* <Button 
								icon={<ReloadOutlined />}
								loading={refreshing}
								onClick={this.handleRefresh}
							>
								Refresh
							</Button> */}
							{/* <Button 
								icon={<ExportOutlined />}
								onClick={this.handleExport}
							>
								Export
							</Button> */}
						</div>
					</div>

					{/* Main Content Area - Tabs */}
					<div className="main-content">
						<Tabs
							activeKey={activeTab}
							onChange={this.handleTabChange}
							items={tabItems}
							size="large"
							className="stream-management-tabs"
							tabBarStyle={{
								marginBottom: 24,
								borderBottom: '1px solid var(--background-tertiary)'
							}}
						/>
					</div>
				</div>
			</Animated>
		);
	}
}

const mapStateToProps = (state) => ({
	// Map Redux state to props if needed
	user: state.user,
	settings: state.settings
});

const mapDispatchToProps = (dispatch) => {
	return bindActionCreators({
		liveprogramList,
	}, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(StreamManagement);
